import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, User as UserIcon, Wallet, FileText, Clock, Save, Phone } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { getUser, updateUser } from '@/api/users'
import { getMySalary, money } from '@/api/payroll'
import { myDocuments } from '@/api/documents'
import AttendanceTimeline from '@/components/AttendanceTimeline'
import type { UserUpdate } from '@/types'

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'

export default function UserSettings() {
  const { user: authUser } = useAuthStore()
  const qc = useQueryClient()
  const uid = authUser?.id

  const { data: me } = useQuery({ queryKey: ['user', uid], queryFn: () => getUser(uid!), enabled: !!uid })

  const [form, setForm] = useState<UserUpdate>({})
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (me) setForm({
      phone: me.phone || '',
      date_of_birth: me.date_of_birth || '',
      address: me.address || '',
      emergency_contact_name: me.emergency_contact_name || '',
      emergency_contact_phone: me.emergency_contact_phone || '',
      timezone: me.timezone || '',
    })
  }, [me])

  const set = (patch: Partial<UserUpdate>) => setForm((f) => ({ ...f, ...patch }))
  const orNull = (v?: string | null) => (v ? v : null)

  const profileMut = useMutation({
    mutationFn: () => updateUser(uid!, {
      phone: orNull(form.phone),
      date_of_birth: orNull(form.date_of_birth),
      address: orNull(form.address),
      emergency_contact_name: orNull(form.emergency_contact_name),
      emergency_contact_phone: orNull(form.emergency_contact_phone),
      timezone: form.timezone || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', uid] })
      setProfileMsg({ ok: true, text: 'Profile updated.' })
      setTimeout(() => setProfileMsg(null), 2500)
    },
    onError: (e: any) => setProfileMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to save' }),
  })

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwMsg, setPwMsg] = useState<string | null>(null)
  const passwordMutation = useMutation({
    mutationFn: async () => {
      if (!newPassword || newPassword.length < 8) throw new Error('Password must be at least 8 characters')
      if (newPassword !== confirmPassword) throw new Error('Passwords do not match')
      await updateUser(uid!, { password: newPassword })
    },
    onSuccess: () => { setPwMsg('Password updated successfully!'); setNewPassword(''); setConfirmPassword('') },
    onError: (error: any) => setPwMsg(error?.response?.data?.detail || error?.message || 'Failed to update password'),
  })

  const role = (me?.role || authUser?.role || '').replace('_', ' ')

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Identity header */}
      <div className="flex items-center gap-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#EC5B2C]/10">
          {me?.avatar_url ? <img src={me.avatar_url} className="h-14 w-14 rounded-full" alt={me.name} /> : <UserIcon className="h-7 w-7 text-[#EC5B2C]" />}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900">{me?.name || authUser?.name}</h1>
            {me?.employee_id && <span className="rounded-full bg-[#EC5B2C]/10 px-2 py-0.5 text-xs font-semibold text-[#EC5B2C]">{me.employee_id}</span>}
            {role && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium capitalize text-gray-600">{role}</span>}
          </div>
          <p className="truncate text-sm text-gray-500">{me?.email || authUser?.email}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Editable profile (2 cols) */}
        <form
          onSubmit={(e) => { e.preventDefault(); setProfileMsg(null); profileMut.mutate() }}
          className="space-y-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5 lg:col-span-2"
        >
          <h2 className="flex items-center gap-2 font-semibold text-gray-900"><Phone className="h-4 w-4 text-[#EC5B2C]" /> Contact & Personal</h2>
          <p className="-mt-2 text-xs text-gray-400">You can edit these yourself. Name, role, salary and other details are managed by an admin.</p>
          {profileMsg && (
            <div className={`rounded px-3 py-2 text-sm ${profileMsg.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{profileMsg.text}</div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Phone"><input className={inputCls} value={form.phone || ''} onChange={(e) => set({ phone: e.target.value })} /></Field>
            <Field label="Date of Birth"><input type="date" className={inputCls} value={form.date_of_birth || ''} onChange={(e) => set({ date_of_birth: e.target.value })} /></Field>
            <Field label="Timezone"><input className={inputCls} placeholder="e.g. Asia/Karachi" value={form.timezone || ''} onChange={(e) => set({ timezone: e.target.value })} /></Field>
          </div>
          <Field label="Address"><textarea rows={2} className={inputCls} value={form.address || ''} onChange={(e) => set({ address: e.target.value })} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Emergency Contact Name"><input className={inputCls} value={form.emergency_contact_name || ''} onChange={(e) => set({ emergency_contact_name: e.target.value })} /></Field>
            <Field label="Emergency Contact Phone"><input className={inputCls} value={form.emergency_contact_phone || ''} onChange={(e) => set({ emergency_contact_phone: e.target.value })} /></Field>
          </div>
          <button type="submit" disabled={profileMut.isPending} className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
            <Save className="h-4 w-4" /> Save Changes
          </button>
        </form>

        {/* Right column: compensation + password */}
        <div className="space-y-6">
          <MyCompensationCard />

          <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
            <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-900"><Lock className="h-4 w-4 text-[#EC5B2C]" /> Change Password</h2>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setPwMsg(null); passwordMutation.mutate() }}>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} placeholder="New password (min 8)" required minLength={8} />
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputCls} placeholder="Confirm password" required minLength={8} />
              {pwMsg && <div className={`text-sm ${pwMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{pwMsg}</div>}
              <button type="submit" disabled={passwordMutation.isPending} className="w-full rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                {passwordMutation.isPending ? 'Updating…' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Documents */}
      <MyDocumentsCard />

      {/* Attendance */}
      {uid && (
        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900"><Clock className="h-4 w-4 text-[#EC5B2C]" /> My Attendance</h2>
            <span className="text-xs text-gray-400">Last 30 days</span>
          </div>
          <AttendanceTimeline userId={uid} />
        </div>
      )}
    </div>
  )
}

function MyCompensationCard() {
  const { data: salary } = useQuery({ queryKey: ['my-salary'], queryFn: () => getMySalary() })
  const cur = salary?.currency || 'PKR'
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
      <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-900"><Wallet className="h-4 w-4 text-[#EC5B2C]" /> Compensation</h2>
      {!salary ? (
        <p className="text-sm text-gray-400">Not set yet.</p>
      ) : (
        <div>
          <div className="grid grid-cols-2 gap-2">
            <MiniStat label="Basic" value={money(salary.basic, cur)} />
            <MiniStat label="Per Hour" value={money(salary.per_hour_rate, cur)} />
            <MiniStat label="Allowances" value={money(salary.allowances_total, cur)} />
            <MiniStat label="Gross / month" value={money(salary.gross, cur)} highlight />
          </div>
          <p className="mt-2 text-xs text-gray-400">Effective {salary.effective_from}</p>
        </div>
      )}
    </div>
  )
}

function MyDocumentsCard() {
  const { data: docs = [] } = useQuery({ queryKey: ['my-documents'], queryFn: () => myDocuments() })
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
      <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-900"><FileText className="h-4 w-4 text-[#EC5B2C]" /> My Documents</h2>
      {docs.length === 0 ? (
        <p className="text-sm text-gray-400">No documents on file.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 py-2.5">
              <div className="min-w-0">
                {d.document_url
                  ? <a href={d.document_url} target="_blank" rel="noreferrer" className="block truncate text-sm font-medium text-gray-900 hover:text-[#EC5B2C]">{d.name}</a>
                  : <span className="block truncate text-sm font-medium text-gray-900">{d.name}</span>}
                <span className="text-xs capitalize text-gray-400">{d.doc_type.replace('_', ' ')}{d.expiry_date ? ` · exp ${d.expiry_date}` : ''}</span>
              </div>
              <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium capitalize text-gray-600">{d.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  )
}

function MiniStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${highlight ? 'bg-[#EC5B2C]/10' : 'bg-gray-50'}`}>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-sm font-bold ${highlight ? 'text-[#EC5B2C]' : 'text-gray-900'}`}>{value}</div>
    </div>
  )
}
